import {NavLink} from 'react-router-dom';
import {Radio, Receipt, SendHorizontal} from 'lucide-react';
import {cn} from '@/lib/utils/cn';
import {ProviderSwitcher} from './ProviderSwitcher';
import {useCurrentProvider} from '@/lib/providers/useCurrentProvider';
import type {ProviderKind} from '@/config/providers';

// Navigation is per provider family: document providers get the transactions
// table, schedule providers get the transmission log. Paths are relative to the
// active provider and resolved against its slug below.
const NAV_BY_KIND: Record<
  ProviderKind,
  Array<{path: string; label: string; icon: typeof Receipt}>
> = {
  document: [{path: 'transactions', label: 'Transactions', icon: Receipt}],
  schedule: [
    {path: 'transmissions', label: 'Transmissions', icon: SendHorizontal},
  ],
};

export function Sidebar() {
  const provider = useCurrentProvider();
  const nav = NAV_BY_KIND[provider.kind];

  return (
    <aside className="flex flex-col w-64 shrink-0 border-r bg-card/40 backdrop-blur-sm">
      <div className="h-16 flex items-center gap-2 px-5 border-b">
        <div className="h-9 w-9 rounded-lg bg-brand-green/20 flex items-center justify-center ring-1 ring-brand-green/30">
          <Radio className="h-5 w-5 text-brand-green" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold tracking-tight">
            Cloud Integration
          </span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
            Dashboard
          </span>
        </div>
      </div>

      <div className="p-3 border-b">
        <ProviderSwitcher />
      </div>

      <nav className="flex-1 p-3 space-y-1">
        {nav.map(({path, label, icon: Icon}) => (
          <NavLink
            key={path}
            to={`/${provider.id}/${path}`}
            className={({isActive}) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 text-[11px] text-muted-foreground border-t">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
          All systems operational
        </div>
      </div>
    </aside>
  );
}
